import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Inbox as InboxIcon, Users, GraduationCap, Clock, LogOut, MapPin, UserX, FileText, Zap, Send, Bell, BellOff, Menu, X, Ban, ShieldCheck, UserMinus, Megaphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import ComposeModal from "@/components/ComposeModal";
import { registerServiceWorker, subscribeToPush, unsubscribeFromPush, currentPermission, pushSupported } from "@/lib/pwa";

const NAV = [
  { to: "/inbox", label: "Inbox", icon: InboxIcon, testId: "nav-inbox", badge: "unread" },
  { to: "/students", label: "Students", icon: GraduationCap, testId: "nav-students" },
  { to: "/classes", label: "Classes", icon: GraduationCap, testId: "nav-classes" },
  { to: "/locations", label: "Locations", icon: MapPin, testId: "nav-locations" },
  { to: "/dropped", label: "Dropped", icon: UserMinus, testId: "nav-dropped" },
  { to: "/absences", label: "Absences", icon: UserX, testId: "nav-absences" },
  { to: "/inactive", label: "Inactive", icon: UserX, testId: "nav-inactive" },
  { to: "/opted-out", label: "Opted Out", icon: Ban, testId: "nav-opted-out", badge: "opted_out" },
  { to: "/broadcasts", label: "Broadcasts", icon: Megaphone, testId: "nav-broadcasts" },
  { to: "/staff-group", label: "Staff Group", icon: Users, testId: "nav-staff-group" },
  { to: "/scheduled", label: "Scheduled", icon: Clock, testId: "nav-scheduled" },
  { to: "/templates", label: "Templates", icon: FileText, testId: "nav-templates" },
  { to: "/automations", label: "Automations", icon: Zap, testId: "nav-automations" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [composeOpen, setComposeOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [optedOutCount, setOptedOutCount] = useState(0);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [canPush, setCanPush] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  const refreshUnread = async () => {
    try {
      const { data } = await api.get("/inbox/summary");
      setUnread(data.unread_total || 0);
      setOptedOutCount(data.opted_out_total || 0);
    } catch (_) { /* silent */ }
  };

  useEffect(() => {
    (async () => {
      await registerServiceWorker();
      const supp = await pushSupported();
      setCanPush(!!supp);
      if (supp) {
        const perm = await currentPermission();
        if (perm === "granted" && "serviceWorker" in navigator) {
          const reg = await navigator.serviceWorker.ready;
          const sub = await reg.pushManager.getSubscription();
          setPushOn(!!sub);
        }
      }
    })();
    refreshUnread();
    const iv = setInterval(refreshUnread, 15000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    document.title = unread > 0 ? `(${unread}) STS Connect` : "STS Connect";
  }, [unread]);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushOn) {
        await unsubscribeFromPush();
        setPushOn(false);
        toast.success("Notifications turned off");
      } else {
        await subscribeToPush();
        setPushOn(true);
        toast.success("Notifications on — you'll be pinged on new texts");
      }
    } catch (e) {
      toast.error(e.message || "Couldn't toggle notifications");
    } finally {
      setPushBusy(false);
    }
  };

  const SidebarBody = ({ onNavigate }) => (
    <>
      <div className="h-16 px-5 flex items-center gap-3 border-b border-slate-200 shrink-0">
        <img src="/logo.png" alt="South Texas Strutters" className="w-11 h-11 object-contain" />
        <div className="leading-tight flex-1">
          <div className="font-bold text-slate-900 text-[15px]">STS Connect</div>
          <div className="text-[11px] text-slate-500 tracking-wide uppercase">South Texas Strutters</div>
        </div>
        <button
          type="button"
          onClick={() => setDrawerOpen(false)}
          className="md:hidden text-slate-500 hover:text-slate-900 p-1"
          data-testid="drawer-close-button"
          aria-label="Close menu"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="px-3 pt-3">
        <Button
          onClick={() => { setComposeOpen(true); onNavigate && onNavigate(); }}
          className="w-full h-11 bg-[#D9232E] hover:bg-[#B91C25] text-white font-semibold shadow-sm"
          data-testid="send-text-button"
        >
          <Send className="w-4 h-4 mr-2" /> Send Text
        </Button>
      </div>

      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {NAV.filter((n) => !n.adminOnly || user?.role === "admin").map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            data-testid={n.testId}
            onClick={() => onNavigate && onNavigate()}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                isActive
                  ? "bg-red-50 text-[#D9232E] border-l-4 border-[#D9232E] pl-2"
                  : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              }`
            }
          >
            <n.icon className="w-[18px] h-[18px]" strokeWidth={2} />
            <span className="flex-1">{n.label}</span>
            {n.badge === "unread" && unread > 0 && (
              <span
                className="min-w-[20px] h-5 px-1.5 rounded-full bg-[#D9232E] text-white text-[11px] font-bold flex items-center justify-center"
                data-testid="sidebar-unread-badge"
              >
                {unread > 99 ? "99+" : unread}
              </span>
            )}
            {n.badge === "opted_out" && optedOutCount > 0 && (
              <span
                className="min-w-[20px] h-5 px-1.5 rounded-full bg-slate-800 text-white text-[11px] font-bold flex items-center justify-center"
                data-testid="sidebar-opted-out-badge"
              >
                {optedOutCount > 99 ? "99+" : optedOutCount}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      {canPush && (
        <div className="px-3 pb-2">
          <button
            type="button"
            onClick={togglePush}
            disabled={pushBusy}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium border transition-colors ${
              pushOn
                ? "bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
                : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100"
            }`}
            data-testid="push-toggle-button"
          >
            {pushOn ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
            {pushOn ? "Notifications on" : "Turn on notifications"}
          </button>
        </div>
      )}

      <div className="border-t border-slate-200 p-3">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center text-xs font-semibold">
            {(user?.email || "?").slice(0, 2).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold text-slate-900 truncate">{user?.name || "Admin"}</div>
            <div className="text-[11px] text-slate-500 truncate">{user?.email}</div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="w-8 h-8 text-slate-500 hover:text-[#D9232E]"
            onClick={doLogout}
            data-testid="logout-button"
            title="Sign out"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-white">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-60 border-r border-slate-200 bg-white flex-col shrink-0" data-testid="sidebar">
        <SidebarBody />
      </aside>

      {/* Mobile drawer overlay */}
      {drawerOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/40 z-40"
          onClick={() => setDrawerOpen(false)}
          data-testid="drawer-overlay"
        />
      )}
      {/* Mobile drawer */}
      <aside
        className={`md:hidden fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] bg-white border-r border-slate-200 flex flex-col shadow-xl transition-transform duration-200 ${drawerOpen ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="mobile-drawer"
      >
        <SidebarBody onNavigate={() => setDrawerOpen(false)} />
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <header className="md:hidden h-14 shrink-0 border-b border-slate-200 bg-white flex items-center gap-3 px-3" data-testid="mobile-topbar">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="p-2 -ml-1 text-slate-700 hover:bg-slate-100 rounded-md"
            data-testid="mobile-menu-button"
            aria-label="Open menu"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <img src="/logo.png" alt="" className="w-7 h-7 object-contain" />
            <div className="font-bold text-slate-900 text-sm truncate">STS Connect</div>
            {unread > 0 && (
              <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-[#D9232E] text-white text-[11px] font-bold flex items-center justify-center">
                {unread > 99 ? "99+" : unread}
              </span>
            )}
          </div>
          <Button
            onClick={() => setComposeOpen(true)}
            className="h-9 px-3 bg-[#D9232E] hover:bg-[#B91C25] text-white text-xs font-semibold"
            data-testid="mobile-send-text-button"
          >
            <Send className="w-3.5 h-3.5 mr-1" /> Send
          </Button>
        </header>

        <main className="flex-1 overflow-hidden min-w-0">
          <Outlet />
        </main>
      </div>

      <ComposeModal open={composeOpen} onOpenChange={setComposeOpen} onSent={refreshUnread} />
    </div>
  );
}
